import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { DOFUS_CLASSES, classLabel } from '../../config/dofusClasses';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import { fmtDate, errorOf, typeLabel, StatusChip, RequestDetails } from './personnageUtils';
import './Personnages.css';

const normalize = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/*
 * ── Attribution directe d'un personnage à un compte Discord (sans demande à valider) ──
 * `player` ({ id, name }) : joueur déjà choisi (bouton + du recensement), il ne reste que le personnage à saisir.
 */
const AssignModal = ({ serverId, player = null, onClose, onAssigned }) => {
    const { t } = useLanguage();
    const { success, error: showError } = useNotification();
    const [members, setMembers] = useState(null);
    const [memberSearch, setMemberSearch] = useState('');
    const [userId, setUserId] = useState(player?.id || '');
    const [form, setForm] = useState({ name: '', classe: '' });
    const [saving, setSaving] = useState(false);
    const nameInput = useRef(null);
    const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    useEffect(() => {
        api.get(`/personnages/${serverId}/members`)
            .then(res => setMembers(res.data.members || []))
            .catch(() => setMembers([]));
    }, [serverId]);

    // Recherche par pseudo ; un identifiant Discord collé tel quel est aussi accepté
    const typedId = /^\d{5,25}$/.test(memberSearch.trim()) ? memberSearch.trim() : '';
    const matches = useMemo(() => {
        const q = normalize(memberSearch.trim());
        return (members || []).filter(m => !q || normalize(m.name).includes(q) || m.id.includes(q)).slice(0, 50);
    }, [members, memberSearch]);
    // Un joueur lié par son seul ID Discord n'est pas forcément dans la liste des joueurs vus par le bot
    const selected = (members || []).find(m => m.id === userId) || (player && player.id === userId ? player : null);
    const targetId = userId || typedId;

    const save = async (e) => {
        e.preventDefault();
        if (!targetId) return;
        setSaving(true);
        try {
            const sent = form;
            await api.post(`/personnages/${serverId}/assign`, { userId: targetId, ...sent });
            success(t('personnages.assigned', '{name} attribué', { name: sent.name }));
            // Joueur déjà choisi : le formulaire reste ouvert pour enchaîner ses autres personnages
            // (sans effacer un nom tapé pendant l'envoi)
            if (player) {
                setForm(prev => (prev.name === sent.name ? { name: '', classe: '' } : prev));
                nameInput.current?.focus();
            }
            onAssigned(!player);
        } catch (err) {
            showError(errorOf(err, t('personnages.decideError', 'Action impossible')));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="c2-modal-backdrop" role="presentation">
            <div className="c2-card c2-modal" role="dialog" aria-modal="true" aria-labelledby="perso-assign-title">
                <div className="c2-modal__head">
                    <h2 id="perso-assign-title">{t('personnages.assignTitle', 'Attribuer un personnage')}</h2>
                    <button type="button" className="c2-icon-btn" onClick={onClose} aria-label={t('common.close', 'Fermer')}>
                        <Icon name="close" size={20} />
                    </button>
                </div>
                <form className="perso-form" onSubmit={save}>
                    <div className="c2-field">
                        <span className="c2-field__label">{t('personnages.player', 'Joueur')}</span>
                        {selected ? (
                            <div className="perso-assign__selected">
                                <strong>{selected.name}</strong>
                                <span className="c2-muted">{selected.id}</span>
                                <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={() => setUserId('')}>{t('personnages.changePlayer', 'Changer')}</button>
                            </div>
                        ) : (
                            <>
                                <input className="c2-input" type="search" value={memberSearch} onChange={e => setMemberSearch(e.target.value)} autoFocus
                                    placeholder={t('personnages.memberSearch', 'Pseudo ou ID Discord...')} />
                                {members === null ? (
                                    <span className="c2-field__hint">{t('common.loading', 'Chargement...')}</span>
                                ) : (
                                    <ul className="perso-assign__members">
                                        {matches.map(m => (
                                            <li key={m.id}>
                                                <button type="button" onClick={() => setUserId(m.id)}>
                                                    <strong>{m.name}</strong><span className="c2-muted">{m.id}</span>
                                                </button>
                                            </li>
                                        ))}
                                        {matches.length === 0 && !typedId && <li className="c2-muted">{t('personnages.noMember', 'Aucun joueur trouvé : colle son ID Discord.')}</li>}
                                    </ul>
                                )}
                                <span className="c2-field__hint">
                                    {typedId
                                        ? t('personnages.useTypedId', 'Le personnage sera attribué au compte {id}.', { id: typedId })
                                        : t('personnages.memberHint', 'Joueurs déjà vus par le bot sur ce serveur. Sinon, colle l\'ID Discord (clic droit sur le pseudo → Copier l\'identifiant).')}
                                </span>
                            </>
                        )}
                    </div>
                    <label className="c2-field">
                        <span className="c2-field__label">{t('personnages.name', 'Nom du personnage')}</span>
                        <input className="c2-input" value={form.name} onChange={e => setField('name', e.target.value)} required maxLength={40} autoFocus={!!player} ref={nameInput} />
                    </label>
                    <label className="c2-field">
                        <span className="c2-field__label">{t('personnages.classe', 'Classe')}</span>
                        <select className="c2-select" value={form.classe} onChange={e => setField('classe', e.target.value)}>
                            <option value="">—</option>
                            {DOFUS_CLASSES.map(c => <option key={c.key} value={c.key}>{t(`classes.${c.key}`, c.label)}</option>)}
                        </select>
                    </label>
                    <div className="c2-modal__actions">
                        <button type="button" className="c2-btn c2-btn--ghost" onClick={onClose} disabled={saving}>{t('common.cancel', 'Annuler')}</button>
                        <button type="submit" className="c2-btn c2-btn--primary" disabled={saving || !targetId || !form.name.trim()}>
                            <Icon name="check" size={18} />{t('personnages.assign', 'Attribuer')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/* ── Modification directe d'un personnage (nom, classe), appliquée sans demande à valider ── */
const EditModal = ({ serverId, personnage, onClose, onSaved }) => {
    const { t } = useLanguage();
    const { success, error: showError } = useNotification();
    const [form, setForm] = useState({ name: personnage.name, classe: personnage.classe || '' });
    const [saving, setSaving] = useState(false);
    const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
    const unchanged = form.name.trim() === personnage.name && form.classe === (personnage.classe || '');

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.patch(`/personnages/${serverId}/personnages/${personnage._id}`, form);
            success(t('personnages.saved', '{name} modifié', { name: form.name.trim() }));
            onSaved();
        } catch (err) {
            showError(errorOf(err, t('personnages.decideError', 'Action impossible')));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="c2-modal-backdrop" role="presentation">
            <div className="c2-card c2-modal" role="dialog" aria-modal="true" aria-labelledby="perso-edit-title">
                <div className="c2-modal__head">
                    <h2 id="perso-edit-title">{t('personnages.editTitle', 'Modifier {name}', { name: personnage.name })}</h2>
                    <button type="button" className="c2-icon-btn" onClick={onClose} aria-label={t('common.close', 'Fermer')}>
                        <Icon name="close" size={20} />
                    </button>
                </div>
                <form className="perso-form" onSubmit={save}>
                    {personnage.owners.length > 1 && (
                        <p className="c2-muted">{t('personnages.editSharedHint', 'Ce personnage est partagé : la modification s\'applique pour tous ses joueurs.')}</p>
                    )}
                    <label className="c2-field">
                        <span className="c2-field__label">{t('personnages.name', 'Nom du personnage')}</span>
                        <input className="c2-input" value={form.name} onChange={e => setField('name', e.target.value)} required maxLength={40} autoFocus />
                        <span className="c2-field__hint">{t('personnages.nameHint', 'Exactement comme en jeu : c\'est ce nom que le bot lira sur les screens.')}</span>
                    </label>
                    <label className="c2-field">
                        <span className="c2-field__label">{t('personnages.classe', 'Classe')}</span>
                        <select className="c2-select" value={form.classe} onChange={e => setField('classe', e.target.value)}>
                            <option value="">—</option>
                            {DOFUS_CLASSES.map(c => <option key={c.key} value={c.key}>{t(`classes.${c.key}`, c.label)}</option>)}
                        </select>
                    </label>
                    <div className="c2-modal__actions">
                        <button type="button" className="c2-btn c2-btn--ghost" onClick={onClose} disabled={saving}>{t('common.cancel', 'Annuler')}</button>
                        <button type="submit" className="c2-btn c2-btn--primary" disabled={saving || unchanged || !form.name.trim()}>
                            <Icon name="check" size={18} />{t('personnages.save', 'Enregistrer')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const PersonnagesAdminPage = () => {
    const { serverId } = useParams();
    const { t } = useLanguage();
    const { success, error: showError } = useNotification();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [search, setSearch] = useState('');
    const [classFilter, setClassFilter] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    const [assigning, setAssigning] = useState(false);   // true : formulaire vide ; { id, name } : joueur déjà choisi
    const [editing, setEditing] = useState(null);        // personnage à modifier

    const load = useCallback(() => {
        setLoading(true);
        return api.get(`/personnages/${serverId}/admin`)
            .then(res => setData(res.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [serverId]);
    useEffect(() => { load(); }, [load]);

    const names = useMemo(() => data?.names || {}, [data]);
    const nameOf = useCallback((id, fallback) => names[id] || fallback || t('personnages.unknownPlayer', 'Joueur {id}', { id: String(id).slice(-4) }), [names, t]);

    // Recensement : un joueur → ses personnages
    const players = useMemo(() => {
        const byPlayer = new Map();
        (data?.personnages || []).forEach(p => p.owners.forEach(ownerId => {
            if (!byPlayer.has(ownerId)) byPlayer.set(ownerId, []);
            byPlayer.get(ownerId).push(p);
        }));
        return [...byPlayer.entries()]
            .map(([userId, personnages]) => ({ userId, name: nameOf(userId), personnages }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [data, nameOf]);

    const visiblePlayers = useMemo(() => {
        const q = normalize(search.trim());
        return players
            .map(player => {
                const playerMatch = !q || normalize(player.name).includes(q);
                const personnages = player.personnages.filter(p =>
                    (!classFilter || p.classe === classFilter) && (playerMatch || normalize(p.name).includes(q)));
                return { ...player, personnages };
            })
            .filter(player => player.personnages.length > 0);
    }, [players, search, classFilter]);

    const kpis = useMemo(() => ({
        players: players.length,
        personnages: data?.personnages.length || 0,
        shared: (data?.personnages || []).filter(p => p.owners.length > 1).length,
        pending: data?.pending.length || 0
    }), [players, data]);

    const decide = async (request, action) => {
        let reason = '';
        if (action === 'reject') {
            const answer = window.prompt(t('personnages.rejectPrompt', 'Motif du refus (facultatif, visible par le joueur) :'), '');
            if (answer === null) return;
            reason = answer;
        }
        setBusyId(request._id);
        try {
            await api.post(`/personnages/${serverId}/requests/${request._id}/${action}`, { reason });
            success(action === 'approve' ? t('personnages.approved', 'Demande validée') : t('personnages.rejected', 'Demande refusée'));
            load();
        } catch (err) {
            showError(errorOf(err, t('personnages.decideError', 'Action impossible')));
        } finally {
            setBusyId(null);
        }
    };

    const unlink = async (personnage, userId) => {
        if (!window.confirm(t('personnages.confirmUnlink', 'Retirer {name} des personnages de {player} ?', { name: personnage.name, player: nameOf(userId) }))) return;
        try {
            await api.delete(`/personnages/${serverId}/personnages/${personnage._id}/owners/${userId}`);
            success(t('personnages.unlinked', 'Lien retiré'));
            load();
        } catch (err) {
            showError(errorOf(err, t('personnages.decideError', 'Action impossible')));
        }
    };

    return (
        <div className="c2-scope c2-admin-page">
            <PageHeader
                icon="users"
                title={t('navigation.personnages', 'Personnages')}
                subtitle={t('personnages.adminSubtitle', 'Recensement des personnages de chaque joueur et validation de leurs demandes')}
                actions={
                    <>
                        <button type="button" className="c2-btn c2-btn--ghost" onClick={load} disabled={loading}>
                            <Icon name="refresh" size={18} />{t('common.refresh', 'Actualiser')}
                        </button>
                        <button type="button" className="c2-btn c2-btn--primary" onClick={() => setAssigning(true)} disabled={!data}>
                            <Icon name="plus" size={18} />{t('personnages.assignTitle', 'Attribuer un personnage')}
                        </button>
                    </>
                }
            />

            {loading && !data ? (
                <div className="c2-loading"><div className="c2-spinner" /><p>{t('common.loading', 'Chargement...')}</p></div>
            ) : !data ? (
                <p className="c2-empty">{t('personnages.loadError', 'Impossible de charger les personnages.')}</p>
            ) : (
                <>
                    <div className="c2-kpis">
                        <div className="c2-kpi"><span className="c2-kpi__value">{kpis.pending}</span><span className="c2-kpi__label">{t('personnages.kpiPending', 'Demandes en attente')}</span></div>
                        <div className="c2-kpi"><span className="c2-kpi__value">{kpis.players}</span><span className="c2-kpi__label">{t('personnages.kpiPlayers', 'Joueurs recensés')}</span></div>
                        <div className="c2-kpi"><span className="c2-kpi__value">{kpis.personnages}</span><span className="c2-kpi__label">{t('personnages.kpiPersonnages', 'Personnages')}</span></div>
                        <div className="c2-kpi"><span className="c2-kpi__value">{kpis.shared}</span><span className="c2-kpi__label">{t('personnages.kpiShared', 'Personnages partagés')}</span></div>
                    </div>

                    <section className="c2-card c2-section">
                        <div className="c2-section__head">
                            <h2 className="c2-section__title"><Icon name="clipboard" size={20} />{t('personnages.pendingTitle', 'Demandes à valider')}</h2>
                        </div>
                        {data.pending.length === 0 ? (
                            <p className="c2-muted">{t('personnages.noPending', 'Aucune demande en attente.')}</p>
                        ) : (
                            <ul className="perso-requests">
                                {data.pending.map(r => {
                                    const others = r.existingOwners.filter(id => id !== r.userId);
                                    return (
                                        <li key={r._id} className="perso-request perso-request--admin">
                                            <strong className="perso-request__player">{nameOf(r.userId, r.userName)}</strong>
                                            <span className="c2-chip c2-chip--purple">{typeLabel(t, r.type)}</span>
                                            <span className="perso-request__details">
                                                <RequestDetails t={t} request={r} />
                                                {others.length > 0 && (
                                                    <span className="perso-request__warn">
                                                        <Icon name="users" size={14} />
                                                        {t('personnages.alreadyPlayedBy', 'Déjà recensé pour {players} : le personnage deviendra partagé', { players: others.map(id => nameOf(id)).join(', ') })}
                                                    </span>
                                                )}
                                            </span>
                                            <span className="c2-muted perso-request__date">{fmtDate(r.createdAt)}</span>
                                            <span className="c2-row">
                                                <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={() => decide(r, 'reject')} disabled={busyId === r._id}>
                                                    <Icon name="close" size={16} />{t('personnages.reject', 'Refuser')}
                                                </button>
                                                <button type="button" className="c2-btn c2-btn--primary c2-btn--sm" onClick={() => decide(r, 'approve')} disabled={busyId === r._id}>
                                                    <Icon name="check" size={16} />{t('personnages.approve', 'Valider')}
                                                </button>
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>

                    <section className="c2-card c2-section">
                        <div className="c2-section__head">
                            <h2 className="c2-section__title"><Icon name="users" size={20} />{t('personnages.census', 'Recensement')}</h2>
                            <div className="perso-filters">
                                <input className="c2-input c2-input--sm" type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('personnages.searchPlaceholder', 'Joueur ou personnage...')} />
                                <select className="c2-select c2-input--sm" value={classFilter} onChange={e => setClassFilter(e.target.value)}>
                                    <option value="">{t('personnages.allClasses', 'Toutes les classes')}</option>
                                    {DOFUS_CLASSES.map(c => <option key={c.key} value={c.key}>{t(`classes.${c.key}`, c.label)}</option>)}
                                </select>
                            </div>
                        </div>
                        {visiblePlayers.length === 0 ? (
                            <p className="c2-muted">{players.length === 0 ? t('personnages.noPlayers', 'Aucun personnage recensé pour l\'instant.') : t('personnages.noMatch', 'Aucun résultat.')}</p>
                        ) : (
                            <div className="c2-table-wrap">
                                <table className="c2-table perso-census">
                                    <thead>
                                        <tr>
                                            <th>{t('personnages.player', 'Joueur')}</th>
                                            <th>{t('personnages.characters', 'Personnages')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visiblePlayers.map(player => (
                                            <tr key={player.userId}>
                                                <td><strong>{player.name}</strong></td>
                                                <td>
                                                    <ul className="perso-chips">
                                                        {player.personnages.map(p => {
                                                            const info = classLabel(t, p.classe);
                                                            const coOwners = p.owners.filter(id => id !== player.userId);
                                                            return (
                                                                <li key={p._id} className={`perso-chip${coOwners.length ? ' perso-chip--shared' : ''}`}
                                                                    title={coOwners.length ? t('personnages.sharedWith', 'Partagé avec {players}', { players: coOwners.map(id => nameOf(id)).join(', ') }) : undefined}>
                                                                    <span className="perso-chip__name">{p.name}</span>
                                                                    {info && <span className="c2-muted">{info}</span>}
                                                                    {coOwners.length > 0 && <Icon name="users" size={13} />}
                                                                    <button type="button" className="perso-chip__edit" onClick={() => setEditing(p)} aria-label={t('personnages.editTitle', 'Modifier {name}', { name: p.name })} title={t('personnages.edit', 'Modifier')}>
                                                                        <Icon name="pencil" size={12} />
                                                                    </button>
                                                                    <button type="button" className="perso-chip__remove" onClick={() => unlink(p, player.userId)} aria-label={t('personnages.unlink', 'Retirer le lien')} title={t('personnages.unlink', 'Retirer le lien')}>
                                                                        <Icon name="close" size={12} />
                                                                    </button>
                                                                </li>
                                                            );
                                                        })}
                                                        <li>
                                                            <button type="button" className="perso-chip perso-chip--add" onClick={() => setAssigning({ id: player.userId, name: player.name })}
                                                                aria-label={t('personnages.addFor', 'Ajouter un personnage à {player}', { player: player.name })} title={t('personnages.addFor', 'Ajouter un personnage à {player}', { player: player.name })}>
                                                                <Icon name="plus" size={14} />
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                    <section className="c2-card c2-section">
                        <div className="c2-section__head">
                            <h2 className="c2-section__title"><Icon name="history" size={20} />{t('personnages.history', 'Dernières décisions')}</h2>
                            <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={() => setShowHistory(v => !v)}>
                                {showHistory ? t('personnages.hide', 'Masquer') : t('personnages.show', 'Afficher ({n})', { n: data.history.length })}
                            </button>
                        </div>
                        {showHistory && (data.history.length === 0 ? (
                            <p className="c2-muted">{t('personnages.noHistory', 'Aucune décision pour l\'instant.')}</p>
                        ) : (
                            <ul className="perso-requests">
                                {data.history.map(r => (
                                    <li key={r._id} className="perso-request perso-request--admin">
                                        <strong className="perso-request__player">{nameOf(r.userId, r.userName)}</strong>
                                        <span className="c2-chip">{typeLabel(t, r.type)}</span>
                                        <span className="perso-request__details">
                                            <RequestDetails t={t} request={r} />
                                            <span className="c2-muted perso-request__by">
                                                {r.byAdmin
                                                    ? (r.type === 'edit'
                                                        ? t('personnages.editedBy', 'modifié directement par {admin}', { admin: nameOf(r.reviewedBy) })
                                                        : t('personnages.assignedBy', 'attribué directement par {admin}', { admin: nameOf(r.reviewedBy) }))
                                                    : t('personnages.reviewedBy', 'par {admin}', { admin: nameOf(r.reviewedBy) })}
                                                {r.reason && ` · ${r.reason}`}
                                            </span>
                                        </span>
                                        <span className="c2-muted perso-request__date">{fmtDate(r.reviewedAt)}</span>
                                        <StatusChip t={t} status={r.status} />
                                    </li>
                                ))}
                            </ul>
                        ))}
                    </section>
                </>
            )}

            {editing && (
                <EditModal
                    serverId={serverId}
                    personnage={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
            {assigning && (
                <AssignModal
                    serverId={serverId}
                    player={assigning === true ? null : assigning}
                    onClose={() => setAssigning(false)}
                    onAssigned={(close) => { if (close) setAssigning(false); load(); }}
                />
            )}
        </div>
    );
};

export default PersonnagesAdminPage;
